import { API_BASE_URL } from '@/lib/api';
import { create } from 'zustand';

export interface Shipment {
  id: number;
  order_id: number;
  currency_id: number;
  marketplace_id: number;
  marketplace_product_id: number;
  reference_item_id: string;
  shipment_id: number | null;
  order_product_waybill_id: number;
  order_product_completion_id: number | null;
  code: string;
  first_identifier: string;
  second_identifier: string;
  sku: string;
  mkt_sku: string | null;
  weight: string;
  ai_weight: string | null;
  mkt_weight: string | null;
  customer_selected_category: string | null;
  price: string;
  qty: number;
  is_cancelled: number;
  refund_date: string | null;
  refund_amount: string | null;
  refund_method: string | null;
  refund_giftcard_id: string | null;
  refund_reason: string | null;
  refund_note: string | null;
  subtotal: string;
  shipping: string;
  mkt_shipping_fee: string | null;
  tax: string;
  mahalkum_fee: string;
  discount_price: number;
  total: string;
  send_to_trakpylot: number;
  product_image: string;
  created_at: string;
  updated_at: string;
  mkt_fee_applied: string | null;
  mkt_tax_included: string | null;
  // Legacy shipment fields
  shid: number;
  shipment_reference: string;
  master_way_bill: string;
  mkt_product_name: string | null;
  mkt_order_number: string | null;
  mkt_name: string | null;
  mkt_url: string;
  orig_tracking_no: string | null;
  order_confirmed: string | null;
}

type ShipmentsResponse = {
  success: boolean;
  data: {
    current_page: number;
    data: Shipment[];
    total: number;
    per_page: number;
    last_page: number;
    from: number;
    to: number;
  };
  total_count: number;
  available_marketplaces: string[];
  message?: string;
};

interface ShipmentFilters {
  search?: string;
  marketplace_filter?: string[];
  date_from?: string;
  date_to?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

interface ShipmentsState {
  shipments: Shipment[];
  total: number;
  availableMarketplaces: string[];
  isLoading: boolean;
  error: string | null;
  fetchShipments: (args: {
    token: string;
    storeId: number;
    page: number;
    limit: number;
    filters?: ShipmentFilters;
  }) => Promise<{ shipments: Shipment[]; total: number; availableMarketplaces: string[] }>;
  fetchIncomingShipments: (args: {
    token: string;
    storeName: string;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: Shipment[]; total: number }>;
  fetchArrivedShipments: (args: {
    token: string;
    storeName: string;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: any[]; total: number }>;
  fetchOutboundDistribution: (args: {
    token: string;
    storeName: string;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: any[]; total: number }>;
  fetchUnconfirmedOrders: (args: {
    token: string;
    storeName: string;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: any[]; total: number }>;
  fetchDistributionIncomingShipments: (args: {
    token: string;
    storeName: string;
    distributionCenterId: number;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: any[]; total: number }>;
  fetchDistributionArrivedShipments: (args: {
    token: string;
    storeName: string;
    distributionCenterId: number;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: any[]; total: number }>;
  fetchDistributionReturnedShipments: (args: {
    token: string;
    storeName: string;
    distributionCenterId: number;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: any[]; total: number }>;
  fetchDistributionMissingShipments: (args: {
    token: string;
    storeName: string;
    distributionCenterId: number;
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{ shipments: any[]; total: number }>;
  reset: () => void;
  clearSearch: () => void;
}

export const useShipmentsStore = create<ShipmentsState>((set) => ({
  shipments: [],
  total: 0,
  availableMarketplaces: [],
  isLoading: false,
  error: null,

  async fetchShipments({ token, storeId, page, limit, filters }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeId) {
        throw new Error('Store ID is required');
      }
      const params = new URLSearchParams({
        store_id: String(storeId),
        page: String(page),
        limit: String(limit),
      });
      if (filters?.search) params.append('search', filters.search);
      if (filters?.marketplace_filter && filters.marketplace_filter.length > 0) {
        filters.marketplace_filter.forEach(marketplace => {
          params.append('marketplace_filter[]', marketplace);
        });
      }
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);
      if (filters?.sort) params.append('sort', filters.sort);
      if (filters?.order) params.append('order', filters.order);

      const res = await fetch(`${API_BASE_URL}/shipments?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Store-ID': String(storeId),
        },
      });
      const json: ShipmentsResponse = await res.json();
      if (!res.ok) {
        throw new Error((json as any)?.message || 'Failed to fetch shipments');
      }
      if (json.success) {
        const result = {
          shipments: json.data.data || [],
          total: json.total_count || json.data.total || 0,
          availableMarketplaces: json.available_marketplaces || [],
        };
        set(result);
        return result;
      } else {
        const result = { shipments: [], total: 0, availableMarketplaces: [] };
        set(result);
        return result;
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch shipments';
      const result = { shipments: [], total: 0, availableMarketplaces: [] };
      set({ error: msg, ...result });
      return result;
    } finally {
      set({ isLoading: false });
    }
  },

  // Mirrors legacy incomingShipments: groups by shipments.id, one row per shipment
  async fetchIncomingShipments({ token, storeName, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) {
        throw new Error('Store name is required');
      }
      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/warehouse/incoming-shipments?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || 'Failed to fetch incoming shipments');
      }
      if (json.success) {
        const rawItems: any[] = json.data?.shipments?.data || [];
        // Normalize legacy field names to what OrdersTable reads
        const shipments = rawItems.map((item: any) => ({
          ...item,
          shipment_reference: item.sh_shipment_reference,
          master_way_bill: item.masterway_bill,
          created_at: item.shcreated_at,
          total: item.total_amount,
        })) as Shipment[];
        const total: number = json.data?.shipments?.total || 0;
        set({ shipments, total, availableMarketplaces: [] });
        return { shipments, total };
      } else {
        set({ shipments: [], total: 0, availableMarketplaces: [] });
        return { shipments: [], total: 0 };
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch incoming shipments';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  // Mirrors legacy local-warehouse arrived: order_confirmed=yes, no distribution_center assigned
  async fetchArrivedShipments({ token, storeName, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) {
        throw new Error('Store name is required');
      }
      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/warehouse/arrived-shipments?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || 'Failed to fetch arrived shipments');
      }
      if (json.success) {
        const rawItems: any[] = json.data?.records?.data || [];
        const total: number = json.data?.records?.total || 0;
        set({ shipments: rawItems as any, total, availableMarketplaces: [] });
        return { shipments: rawItems, total };
      } else {
        set({ shipments: [], total: 0, availableMarketplaces: [] });
        return { shipments: [], total: 0 };
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch arrived shipments';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  // Mirrors legacy local-warehouse/outbound-distribution: batch_id + distribution_center assigned
  async fetchOutboundDistribution({ token, storeName, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) {
        throw new Error('Store name is required');
      }
      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/warehouse/outbound-distribution?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || 'Failed to fetch outbound distribution records');
      }
      if (json.success) {
        const rawItems: any[] = json.data?.records?.data || [];
        const total: number = json.data?.records?.total || 0;
        set({ shipments: rawItems as any, total, availableMarketplaces: [] });
        return { shipments: rawItems, total };
      }

      set({ shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch outbound distribution records';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  // Mirrors legacy local-warehouse/unconfirmed-orders: order_confirmed = no
  async fetchUnconfirmedOrders({ token, storeName, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) {
        throw new Error('Store name is required');
      }
      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/warehouse/unconfirmed-orders?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || 'Failed to fetch unconfirmed orders');
      }
      if (json.success) {
        const rawItems: any[] = json.data?.totalShipmentRecords?.data || [];
        const total: number = json.data?.totalShipmentRecords?.total || 0;
        set({ shipments: rawItems as any, total, availableMarketplaces: [] });
        return { shipments: rawItems, total };
      }

      set({ shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch unconfirmed orders';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  async fetchDistributionIncomingShipments({ token, storeName, distributionCenterId, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) throw new Error('Store name is required');
      if (!distributionCenterId) throw new Error('Distribution center is required');

      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/distribution/incoming-shipments/${distributionCenterId}?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch distribution inbound shipments');

      const payload = json.data?.searchedShipments || json.data?.shipments;
      const rawItems: any[] = payload?.data || [];
      const total: number = payload?.total || 0;
      set({ shipments: rawItems as any, total, availableMarketplaces: [] });
      return { shipments: rawItems, total };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch distribution inbound shipments';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  async fetchDistributionArrivedShipments({ token, storeName, distributionCenterId, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) throw new Error('Store name is required');
      if (!distributionCenterId) throw new Error('Distribution center is required');

      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/distribution/arrived-shipments/${distributionCenterId}?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch distribution in-center shipments');

      const searchedPayload = json.data?.searchedRecords;
      const payload = searchedPayload && Array.isArray(searchedPayload.data)
        ? searchedPayload
        : json.data?.records;
      const rawItems: any[] = payload?.data || [];
      const total: number = payload?.total || 0;
      set({ shipments: rawItems as any, total, availableMarketplaces: [] });
      return { shipments: rawItems, total };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch distribution in-center shipments';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  async fetchDistributionReturnedShipments({ token, storeName, distributionCenterId, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) throw new Error('Store name is required');
      if (!distributionCenterId) throw new Error('Distribution center is required');

      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/distribution/damaged-and-returned/${distributionCenterId}?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch distribution returned shipments');

      const searchedPayload = json.data?.searchedRecords;
      const payload = searchedPayload && Array.isArray(searchedPayload.data)
        ? searchedPayload
        : json.data?.records;
      const rawItems: any[] = payload?.data || [];
      const total: number = payload?.total || 0;
      set({ shipments: rawItems as any, total, availableMarketplaces: [] });
      return { shipments: rawItems, total };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch distribution returned shipments';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  async fetchDistributionMissingShipments({ token, storeName, distributionCenterId, page, limit, search }) {
    set({ isLoading: true, error: null });
    try {
      if (!storeName) throw new Error('Store name is required');
      if (!distributionCenterId) throw new Error('Distribution center is required');

      const params = new URLSearchParams({
        store_name: storeName,
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim() !== '') params.set('search', search.trim());

      const res = await fetch(`${API_BASE_URL}/distribution/unconfirmed-orders/${distributionCenterId}?${params.toString()}`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch distribution missing shipments');

      const payload = json.data?.totalShipmentRecords;
      const rawItems: any[] = payload?.data || [];
      const total: number = payload?.total || 0;
      set({ shipments: rawItems as any, total, availableMarketplaces: [] });
      return { shipments: rawItems, total };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch distribution missing shipments';
      set({ error: msg, shipments: [], total: 0, availableMarketplaces: [] });
      return { shipments: [], total: 0 };
    } finally {
      set({ isLoading: false });
    }
  },

  reset() {
    set({ shipments: [], total: 0, availableMarketplaces: [], isLoading: false, error: null });
  },

  clearSearch() {
    set({ shipments: [], total: 0, availableMarketplaces: [], isLoading: false, error: null });
  },
}));


